import type { AppointmentForm } from '@devexpress/dx-react-scheduler-material-ui';
import { useTheme } from '@mui/material/styles';
import { endOfDay } from 'date-fns/esm';
import dayjs from 'dayjs';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DateInput } from '@components/DateInput';
import RadioButtonNode from '@components/RadioButtons/RadioButtonNode';
import RadioButtons from '@components/RadioButtons/RadioButtons';

import { changeRecurrenceOptions, getRecurrenceOptions } from '@utils/routines';

import Label from '../Label';

const messages = defineMessages({
  on: {
    id: 'endRepeatEditorBase.on',
    defaultMessage: 'On',
  },
  never: {
    id: 'endRepeatEditorBase.never',
    defaultMessage: 'Never',
  },
  occurrences: {
    id: 'endRepeatEditorBase.occurrences',
    defaultMessage: 'occurrences',
  },
  after: {
    id: 'endRepeatEditorBase.after',
    defaultMessage: 'After',
  },
});

const checkIsNaturalNumber = (number: number) =>
  number > 0 && number <= Number.MAX_SAFE_INTEGER;
const isDateValid = (date: Date) => dayjs(date).isValid();

const EndRepeatEditorBase = ({
  textEditorComponent: TextEditor,
  onFieldChange,
  appointmentData,
}: AppointmentForm.RadioGroupProps) => {
  const { formatMessage } = useIntl();
  const [count, setCount] = React.useState(1);
  const [endDate, setEndDate] = React.useState(appointmentData.endDate);

  const { palette } = useTheme();

  const { rRule } = appointmentData;
  const recurrenceOptions = React.useMemo(
    () => (rRule ? getRecurrenceOptions(rRule) : {}),
    [rRule]
  );
  const changeRecurrenceCount = React.useCallback(
    (nextCount: number) =>
      checkIsNaturalNumber(nextCount) &&
      onFieldChange({
        rRule: changeRecurrenceOptions({
          ...recurrenceOptions,
          count: nextCount,
        }),
      }),
    [recurrenceOptions, onFieldChange]
  );
  const changeRecurrenceEndDate = React.useCallback(
    (date: Date) => {
      if (isDateValid(date)) {
        onFieldChange({
          rRule: changeRecurrenceOptions({
            ...recurrenceOptions,
            until: endOfDay(date),
          }),
        });
      }
    },
    [recurrenceOptions, onFieldChange]
  );

  const recurrenceCount = recurrenceOptions.count ?? count;
  const recurrenceEndDate =
    recurrenceOptions.until ?? (endDate ? endOfDay(new Date(endDate)) : null);
  let value: string | null = null;
  if (recurrenceOptions.count !== undefined) {
    value = 'endAfter';
  } else if (recurrenceOptions.until) {
    value = 'endBy';
  } else {
    value = 'never';
  }

  const onRadioGroupValueChange = (
    newValue: boolean | number | string,
    oldValue: string | null
  ) => {
    if (newValue === oldValue) {
      return;
    }
    let change = null;
    switch (newValue) {
      case 'endAfter':
        setEndDate(recurrenceOptions.until ?? endDate);
        change = { count, until: undefined };
        break;
      case 'endBy':
        setCount(recurrenceOptions.count ?? count);
        change = { count: undefined, until: endDate };
        break;
      case 'never':
      default:
        change = { count: undefined, until: undefined };
        break;
    }
    change = {
      ...change,
      until: change.until ? new Date(change.until) : undefined,
    };
    onFieldChange({
      rRule: changeRecurrenceOptions({
        ...recurrenceOptions,
        ...change,
      }),
    });
  };
  return (
    <RadioButtons
      alignment="vertical"
      onChange={(newValue) => onRadioGroupValueChange(newValue, value)}
      selected={value}
      style={{ width: '100%', marginBottom: 14, marginLeft: -5 }}
    >
      <RadioButtonNode style={{ width: '100%' }} value="never">
        <Label
          color={palette.text.secondary}
          text={formatMessage(messages.never)}
        />
      </RadioButtonNode>
      <RadioButtonNode style={{ width: '100%' }} value="endAfter">
        <div
          style={{ display: 'inline-block', margin: 'auto 0', float: 'left' }}
        >
          <Label
            color={palette.text.secondary}
            text={formatMessage(messages.after)}
            mr={1}
          />
        </div>
        <TextEditor
          onValueChange={(value) => {
            changeRecurrenceCount(parseInt(value, 10));
          }}
          placeholder=""
          readOnly={false}
          type="numberEditor"
          value={recurrenceCount}
        />
        <div style={{ display: 'inline-block', margin: 'auto 0' }}>
          <Label
            color={palette.text.secondary}
            text={formatMessage(messages.occurrences)}
            ml={1}
          />
        </div>
      </RadioButtonNode>
      <RadioButtonNode style={{ width: '100%' }} value="endBy">
        <div style={{ display: 'inline-block', margin: 'auto 0' }}>
          <Label
            color={palette.text.secondary}
            text={formatMessage(messages.on)}
            mr={1}
          />
        </div>
        <DateInput
          onChange={(value) => {
            if (value) {
              changeRecurrenceEndDate(value);
            }
          }}
          value={recurrenceEndDate ? recurrenceEndDate.toString() : null}
        />
      </RadioButtonNode>
    </RadioButtons>
  );
};

export default EndRepeatEditorBase;
